import { useState, useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import {
  SidebarGroup,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubItem,
  SidebarMenuSubButton,
  useSidebar,
} from './ui/sidebar';
import { cn } from '@/lib/utils';
import styles from './NavDocuments.module.scss';

interface BaseMenuItem {
  title: string;
  icon: React.ComponentType<{ className?: string }>;
}

interface RegularMenuItem extends BaseMenuItem {
  type: 'item';
  url: string;
}

interface CollapsibleMenuItem extends BaseMenuItem {
  type: 'group';
  items: {
    title: string;
    url: string;
    icon: React.ComponentType<{ className?: string }>;
  }[];
}

type MenuItem = RegularMenuItem | CollapsibleMenuItem;

interface NavDocumentsProps {
  items: MenuItem[];
}

export function NavDocuments({ items }: NavDocumentsProps) {
  const { state, setOpen } = useSidebar();
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (state === 'collapsed') {
      setExpandedGroups({});
    }
  }, [state]);

  const toggleGroup = (title: string) => {
    // If sidebar is collapsed, expand it first
    if (state === 'collapsed') {
      setOpen(true);
    }

    // Then toggle the group
    setExpandedGroups(prev => ({
      ...prev,
      [title]: !prev[title]
    }));
  };

  return (
    <SidebarGroup>
      <SidebarMenu>
        {items.map((item, index) => {
          if (item.type === 'item') {
            const Icon = item.icon;
            return (
              <SidebarMenuItem key={`${item.title}-${index}`}>
                <NavLink to={item.url} end style={{ textDecoration: 'none' }}>
                  {({ isActive }) => (
                    <SidebarMenuButton
                      isActive={isActive}
                      tooltip={state === 'collapsed' ? item.title : undefined}
                      className={cn(
                        styles.navLink,
                        isActive && styles.navLinkActive
                      )}
                    >
                      <Icon className={styles.navIcon} />
                      <span>{item.title}</span>
                    </SidebarMenuButton>
                  )}
                </NavLink>
              </SidebarMenuItem>
            );
          }

          if (item.type === 'group') {
            const Icon = item.icon;
            const isExpanded = expandedGroups[item.title] || false;

            return (
              <SidebarMenuItem key={`${item.title}-${index}`}>
                <SidebarMenuButton
                  onClick={() => toggleGroup(item.title)}
                  tooltip={state === 'collapsed' ? item.title : undefined}
                  className={styles.navLink}
                >
                  <Icon className={styles.navIcon} />
                  <span>{item.title}</span>
                  <ChevronRight
                    className={cn(
                      styles.chevron,
                      isExpanded && styles.chevronExpanded
                    )}
                  />
                </SidebarMenuButton>

                {/* Sub items */}
                {isExpanded && (
                  <SidebarMenuSub>
                    {item.items.map((subItem, subIndex) => {
                      const SubIcon = subItem.icon;
                      return (
                        <SidebarMenuSubItem key={`${subItem.title}-${subIndex}`}>
                          <NavLink to={subItem.url} style={{ textDecoration: 'none' }}>
                            {({ isActive }) => (
                              <SidebarMenuSubButton
                                isActive={isActive}
                                className={cn(
                                  styles.navSubLink,
                                  isActive && styles.navSubLinkActive
                                )}
                              >
                                <SubIcon className={styles.navIcon} />
                                <span>{subItem.title}</span>
                              </SidebarMenuSubButton>
                            )}
                          </NavLink>
                        </SidebarMenuSubItem>
                      );
                    })}
                  </SidebarMenuSub>
                )}
              </SidebarMenuItem>
            );
          }

          return null;
        })}
      </SidebarMenu>
    </SidebarGroup>
  );
}
